import React from "react";
import { Typography, Tag } from "antd";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { selectEnvironment } from "../store/selector";
import { selectIsDevEnvironment } from "@tailormed/common-client/store/selector";
import { useGetSetting } from "../hooks/getSetting";
import { GOLD, ERROR_600, SUCCESS_600 } from "../constant/colors";

const { Text } = Typography;

// MVP-30839: labels for the dev-only EE submission simulation outcome shown in the demo top bar.
// Mirrors the financial-navigation api/constants/EESimulation.js enum (OTHER is rendered with the
// configured mock name appended instead of the backend's "Other (named mock)" label — see outcomeText).
const EE_SIMULATION_OUTCOME_LABELS = {
  SUCCESS: "Success",
  DUPLICATE: "Duplicate",
  REJECTION: "Rejection",
  ERROR_TM_SIDE: "Error - TM side",
  ERROR_VENDOR_SIDE: "Error - vendor side",
  OTHER: "Other"
};

const StyledContainer = styled.div`
  background-color: ${GOLD};
  text-align: center;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const EnvironmentHeaderComponent = ({ environment, isDevEnvironment }) => {
  const { t } = useTranslation();

  let simulateEnvBehaviour;
  let eeSimulationOutcome;
  let eeSimulationOtherMockName;
  try {
    [simulateEnvBehaviour, eeSimulationOutcome, eeSimulationOtherMockName] = useGetSetting([
      "env.simulateEnvBehaviour",
      "ee.simulation.outcome",
      "ee.simulation.other-mock-name"
    ]);
  } catch (error) {
    console.warn("Failed to get simulate/EE-simulation settings:", error);
    simulateEnvBehaviour = null;
  }

  const isDemoMode = simulateEnvBehaviour === "demo";

  // MVP-30839: surface the configured EE simulation outcome in the demo top bar (e.g. "Demo mode: Rejection").
  const outcome = eeSimulationOutcome || "SUCCESS";
  const outcomeLabel = EE_SIMULATION_OUTCOME_LABELS[outcome] || outcome;
  const outcomeText =
    outcome === "OTHER" && eeSimulationOtherMockName ? `${outcomeLabel} (${eeSimulationOtherMockName})` : outcomeLabel;
  const outcomeColor = outcome === "SUCCESS" ? SUCCESS_600 : ERROR_600;

  const showEnvironmentHeader = environment && isDevEnvironment;

  if (!showEnvironmentHeader) return null;

  return (
    <StyledContainer className="dont-print">
      <Text>{t("environment_header", { environment })}</Text>
      {isDemoMode && (
        <Tag color={outcomeColor} style={{ borderRadius: "6px" }} data-test-id="demo-mode-tag" data-outcome={outcome}>
          {`${t("demo_mode")}: ${outcomeText}`}
        </Tag>
      )}
    </StyledContainer>
  );
};

const mapStateToProps = createStructuredSelector({
  environment: selectEnvironment,
  isDevEnvironment: selectIsDevEnvironment
});

const EnvironmentHeader = connect(mapStateToProps)(EnvironmentHeaderComponent);

export { EnvironmentHeader };
